import { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import type { Project } from '@jisy/shared';
import { SectionMark } from './Badges.js';
import styles from './SectionSelect.module.css';

/**
 * Выбор одного раздела прямо в строке таблицы.
 *
 * От `FilterPopover` отличается не оформлением, а смыслом: там отбор несколькими значениями,
 * здесь — присвоение одного, и выбор закрывает список. Поэтому чекбоксов нет, а «Без раздела»
 * стоит первым пунктом, а не отдельной кнопкой сброса: снять привязку — такой же выбор.
 */
export function SectionSelect({
  projects,
  value,
  onChange,
  disabled = false,
}: {
  projects: Project[];
  value: string | null;
  onChange: (projectId: string | null) => void;
  disabled?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = projects.find((project) => project.id === value) ?? null;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return projects;
    return projects.filter(
      (project) =>
        project.name.toLowerCase().includes(needle) || project.key.toLowerCase().includes(needle),
    );
  }, [projects, search]);

  function choose(projectId: string | null): void {
    setOpen(false);
    setSearch('');
    if (projectId !== value) onChange(projectId);
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className={selected ? styles.trigger : styles.triggerEmpty}
          disabled={disabled}
          title={selected ? `${selected.key} · ${selected.name}` : t('meetings.noSection')}
          // Щелчок по ячейке не должен открывать запись: он выбирает раздел.
          onClick={(event) => event.stopPropagation()}
        >
          {selected ? (
            <>
              <SectionMark colorIndex={selected.colorIndex} />
              <span className={`${styles.name} truncate`}>{selected.name}</span>
            </>
          ) : (
            <span className={styles.placeholder}>{t('meetings.noSection')}</span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.content}
          align="start"
          sideOffset={4}
          onClick={(event) => event.stopPropagation()}
        >
          <input
            className={styles.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('filters.search')}
            autoFocus
          />

          <div className={styles.list} role="listbox" aria-label={t('meetings.columns.section')}>
            <button
              type="button"
              className={styles.option}
              role="option"
              aria-selected={value === null}
              onClick={() => choose(null)}
            >
              <span className={styles.markPlaceholder} aria-hidden="true" />
              <span className={`${styles.optionLabel} truncate`}>{t('meetings.noSection')}</span>
              {value === null && <span className={styles.check}>✓</span>}
            </button>

            {visible.map((project) => (
              <button
                type="button"
                className={styles.option}
                role="option"
                aria-selected={project.id === value}
                key={project.id}
                onClick={() => choose(project.id)}
              >
                <SectionMark colorIndex={project.colorIndex} />
                <span className={`${styles.optionLabel} truncate`}>{project.name}</span>
                {project.id === value && <span className={styles.check}>✓</span>}
              </button>
            ))}

            {visible.length === 0 && <div className={styles.empty}>{t('filters.empty')}</div>}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
